import { KeyboardEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import companiesApi from "@/api/companiesApi";
import CompanySummaryResponse from "@/model/CompanySummaryResponse";
import "@/pages/CompaniesPage/CompaniesPage.scss";

const PAGE_SIZE = 20;

function CompaniesPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<CompanySummaryResponse[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    companiesApi
      .listCompanies(session.accessToken, page, PAGE_SIZE)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setCompanies(result.content ?? []);
        setTotalPages(result.totalPages ?? 0);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, page]);

  if (!session) {
    return null;
  }

  return (
    <div className="companies-page">
      <header className="companies-page__header">
        <div>
          <h1>{t("companies.title")}</h1>
          <p>{t("companies.welcome", { email: session.email })}</p>
        </div>
        <div className="companies-page__header-actions">
          <Link to="/companies/new">{t("companies.createButton")}</Link>
        </div>
      </header>

      {isLoading && <p>{t("companies.loading")}</p>}
      {!isLoading && error && <p role="alert">{t("companies.error")}</p>}
      {!isLoading && !error && companies.length === 0 && (
        <p>{t("companies.empty")}</p>
      )}

      {!isLoading && !error && companies.length > 0 && (
        <>
          <table className="companies-page__table">
            <thead>
              <tr>
                <th>{t("companies.columns.name")}</th>
                <th>{t("companies.columns.city")}</th>
                <th>{t("companies.columns.country")}</th>
                <th>{t("companies.columns.status")}</th>
                <th>{t("companies.columns.createdAt")}</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company, index) => {
                const companyId = company.id;
                const goToCompany = () => {
                  if (companyId) {
                    navigate(`/companies/${companyId}`);
                  }
                };
                const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
                  if (event.key === "Enter") {
                    goToCompany();
                  }
                };

                return (
                  <tr
                    key={companyId ?? index}
                    className="companies-page__row"
                    role={companyId ? "link" : undefined}
                    tabIndex={companyId ? 0 : undefined}
                    onClick={companyId ? goToCompany : undefined}
                    onKeyDown={companyId ? handleKeyDown : undefined}
                  >
                    <td>{company.name ?? "—"}</td>
                    <td>{company.city ?? "—"}</td>
                    <td>{company.country ?? "—"}</td>
                    <td>
                      {company.status ? (
                        <span
                          className={`status-badge status-badge--${company.status.toLowerCase()}`}
                        >
                          {t(`companies.status.${company.status}`)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {company.createdAt
                        ? new Date(company.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="companies-page__pagination">
            <button
              type="button"
              onClick={() => setPage((current) => current - 1)}
              disabled={page <= 0}
            >
              {t("companies.pagination.previous")}
            </button>
            <span>
              {t("companies.pagination.pageInfo", {
                page: page + 1,
                totalPages,
              })}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page + 1 >= totalPages}
            >
              {t("companies.pagination.next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default CompaniesPage;
